import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type {
  AppSelectOption,
  SelectPaginatedData,
} from '@brighterly/lib-core-types/ui/selects/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

import { useTutorsListApi } from '@entities/tutor'
import { pick } from '@shared/lib/collections'
import { useAppSelectPaginated } from '@shared/ui/selects'
import { useToast } from '@shared/ui/toast'

import { useTutorSlotLogsApi } from '../../api/tutor-slots/logs.hook'
import { useTutorSlotLogStore } from '../tutor-slot-log/store'

interface FiltersTutorSlotLogReturn {
  refTutorId: Ref<number | undefined>
  refSelectedTutor: ComputedRef<AppSelectOption<number> | undefined>
  tutorOptions: ComputedRef<AppSelectOption[]>
  tutorHasMore: ComputedRef<boolean>
  tutorLoading: Readonly<Ref<boolean>>
  onTutorSearch: (query: string) => void
  onTutorLoadMore: (query: string) => void
  onTutorOpen: () => void
  refDate: Ref<Date | null>
  refSlotTime: WritableComputedRef<string | undefined>
  slotTimeOptions: AppSelectOption<string>[]
  isLoading: ComputedRef<boolean>
  handleSearch: () => Promise<void>
  handleClear: () => Promise<void>
}

const SLOT_TIME_OPTIONS: AppSelectOption<string>[] = Array.from({ length: 24 }, (_, hour) => {
  const value = `${String(hour).padStart(2, '0')}:00`
  return { text: value, value }
})

export const useFiltersTutorSlotLog = (): FiltersTutorSlotLogReturn => {
  const router = useRouter()
  const store = useTutorSlotLogStore()
  const toast = useToast()

  const refTutorId = computed<number | undefined>({
    get: () => store.getFilterTutorId ?? undefined,
    set: (value) => store.setFilterTutorId(value ?? null),
  })

  const fetcher = ({ query, page, signal }: { query: string, page: number, signal: AbortSignal }): Promise<SelectPaginatedData | null> => {
    const preselectedId = store.getFilterTutorId
    return useTutorsListApi({
      query,
      page,
      signal,
      preselected: preselectedId !== null ? [preselectedId] : undefined,
    })
  }

  const {
    options: tutorOptions,
    hasMore: tutorHasMore,
    loading: tutorLoading,
    onSearch: onTutorSearch,
    onLoadMore: onTutorLoadMore,
    onOpen: onTutorOpen,
  } = useAppSelectPaginated(fetcher, { lazy: true })

  const refSelectedTutor = computed<AppSelectOption<number> | undefined>(() =>
    store.getSelectedTutorOption ?? undefined,
  )

  watch(refTutorId, (id) => {
    if (id === undefined || id === null) {
      store.setSelectedTutorOption(null)
      return
    }
    const option = tutorOptions.value.find(o => o.value === id)
    if (option) store.setSelectedTutorOption(option as AppSelectOption<number>)
  })

  const refDate = ref<Date | null>(
    store.getFilterDate ? dayjs(store.getFilterDate).toDate() : null,
  )
  const refSlotTime = computed<string | undefined>({
    get: () => store.getFilterSlotTime ?? undefined,
    set: (value) => store.setFilterSlotTime(value ?? null),
  })

  watch(refDate, (value) => {
    store.setFilterDate(value ? dayjs(value).format(DateFormat.DefaultDate) : null)
  })

  const isLoading = computed<boolean>(() => store.isLoading)

  /** The logs API takes a tutor and a slot only, so a search without them asks for them instead of a request. */
  const handleSearch = async (): Promise<void> => {
    if (!store.isSearchable) {
      toast.warning({ title: 'Warning', description: 'Choose a tutor, a date and a slot to search the log.' })
      return
    }

    const query = pick({
      tutor_id: store.getFilterTutorId !== null ? String(store.getFilterTutorId) : undefined,
      slot_start: store.getSlotStart ?? undefined,
      page: '1',
      per_page: String(store.getPerPage),
    })

    await router.push({ query })
    await useTutorSlotLogsApi()
  }

  const handleClear = async (): Promise<void> => {
    store.resetFilters()
    refDate.value = null
    await router.push({ query: {} })
  }

  return {
    refTutorId,
    refSelectedTutor,
    tutorOptions,
    tutorHasMore,
    tutorLoading,
    onTutorSearch,
    onTutorLoadMore,
    onTutorOpen,
    refDate,
    refSlotTime,
    slotTimeOptions: SLOT_TIME_OPTIONS,
    isLoading,
    handleSearch,
    handleClear,
  }
}
